import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useLanguage } from '../context/LanguageContext';
import { Sparkles, Video } from 'lucide-react';

const COPY = {
  es: {
    kicker: 'Clases individuales',
    title: 'Aprende el español que tú necesitas',
    paragraphs: [
      'Puede que entiendas bastante español, pero cuando llega el momento de hablar te cueste encontrar las palabras. Quizá conoces la gramática, pero mantener una conversación todavía te resulta difícil.',
      'O puede que necesites español para vivir en España, trabajar, viajar, comunicarte con más confianza o preparar un examen oficial. Por eso mis clases no siguen un programa idéntico para todos: trabajaremos exactamente lo que tú necesitas mejorar.',
    ],
    note: 'Las clases son 100 % online por Zoom o Google Meet.',
  },
  en: {
    kicker: 'One-to-one lessons',
    title: 'Spanish lessons built around your goals',
    paragraphs: [
      'Most of my students come from the same place: they understand far more Spanish than they can say out loud. My lessons are one-to-one and designed around your level, your interests and your reasons for learning — a move to Spain, a job, family, travel or an official certificate.',
      'We practise conversation, vocabulary, grammar, pronunciation and listening in a practical way, with materials I prepare for each lesson. As an accredited DELE examiner I also prepare candidates for the official exams, and I work with students across Europe and beyond, fitting lessons around different time zones.',
    ],
    note: 'Lessons are 100% online via Zoom or Google Meet.',
  },
  de: {
    kicker: 'Einzelunterricht',
    title: 'Spanischunterricht, der zu deinem Alltag passt',
    paragraphs: [
      'Die meisten meiner Lernenden sagen dasselbe: Verstehen geht ganz gut, aber beim Sprechen kommt die Blockade. Mein Unterricht ist Einzelunterricht und richtet sich nach deinem Niveau und deinem Grund zu lernen – ein Umzug nach Spanien, der Beruf, die Familie, Reisen oder ein offizielles Zertifikat.',
      'Wir üben Konversation, Wortschatz, Grammatik, Aussprache und Hörverstehen – praktisch und mit Material, das ich für jede Stunde vorbereite. Als akkreditierte DELE-Prüferin begleite ich dich auch durch die Prüfungsvorbereitung, und wenn du nach Spanien ziehst, üben wir genau die Situationen, die dort auf dich warten.',
    ],
    note: 'Der Unterricht ist 100 % online über Zoom oder Google Meet.',
  },
};

export const HomeIntro = () => {
  const { language } = useLanguage();
  const copy = COPY[language] || COPY.es;
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.15 });

  return (
    <section
      id="clases-online"
      className="py-20 md:py-24 bg-gradient-to-b from-[#FFF0EF] to-white relative overflow-hidden"
      data-testid="home-intro-section"
    >
      <div
        aria-hidden
        className="absolute -top-24 -left-24 w-[32rem] h-[32rem] rounded-full opacity-40 blur-3xl"
        style={{ background: 'radial-gradient(circle, rgba(168,216,234,0.45) 0%, transparent 70%)' }}
      />
      <div ref={ref} className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
        >
          <span className="inline-flex items-center gap-2 text-[#E38987] font-semibold text-xs uppercase tracking-[0.18em]">
            <Sparkles className="w-4 h-4" />
            {copy.kicker}
          </span>
          <h2
            className="text-3xl md:text-4xl font-bold text-[#4A4A4A] mt-4 mb-5 leading-tight"
            data-testid="home-intro-title"
          >
            {copy.title}
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-[#A8D8EA] to-[#F5A6A4] mb-8" />

          <div className="space-y-5 max-w-3xl">
            {copy.paragraphs.map((p, i) => (
              <motion.p
                key={i}
                initial={{ opacity: 0, y: 18 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.15 + i * 0.12 }}
                className="text-base text-gray-600 leading-relaxed"
              >
                {p}
              </motion.p>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.45 }}
            className="mt-8 inline-flex items-center gap-3 px-5 py-3 bg-white rounded-2xl shadow-md border border-[#A8D8EA]/50"
            data-testid="home-intro-note"
          >
            <span className="w-9 h-9 rounded-xl bg-[#7DBDD9] flex items-center justify-center flex-shrink-0">
              <Video className="w-4.5 h-4.5 text-white" />
            </span>
            <span className="text-sm font-semibold text-[#4A4A4A]">{copy.note}</span>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
